import { useQuery } from "@tanstack/react-query";
import {
    UsersIcon,
    TruckIcon,
    MapPinIcon,
    CurrencyDollarIcon,
    ClockIcon,
    MagnifyingGlassIcon,
    ArrowPathIcon,
    ExclamationTriangleIcon,
    CheckCircleIcon,
    XCircleIcon,
    BoltIcon,
} from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/Button";
import { getLivePlatformStats } from "../api/liveMonitoringApi";

// ============================================================================
// Stat Card
// ============================================================================

const StatCard = ({
    label,
    value,
    icon: Icon,
    color = "primary",
    sub,
}: {
    label: string;
    value: number | string;
    icon: React.ElementType;
    color?: string;
    sub?: string;
}) => {
    const colorMap: Record<string, { bg: string; icon: string; text: string }> = {
        primary: { bg: "bg-primary-50 dark:bg-primary-900/20", icon: "text-primary-600 dark:text-primary-400", text: "text-primary-700 dark:text-primary-300" },
        rose: { bg: "bg-rose-50 dark:bg-rose-900/20", icon: "text-rose-600 dark:text-rose-400", text: "text-rose-700 dark:text-rose-300" },
        amber: { bg: "bg-amber-50 dark:bg-amber-900/20", icon: "text-amber-600 dark:text-amber-400", text: "text-amber-700 dark:text-amber-300" },
        emerald: { bg: "bg-emerald-50 dark:bg-emerald-900/20", icon: "text-emerald-600 dark:text-emerald-400", text: "text-emerald-700 dark:text-emerald-300" },
        blue: { bg: "bg-blue-50 dark:bg-blue-900/20", icon: "text-blue-600 dark:text-blue-400", text: "text-blue-700 dark:text-blue-300" },
        orange: { bg: "bg-orange-50 dark:bg-orange-900/20", icon: "text-orange-600 dark:text-orange-400", text: "text-orange-700 dark:text-orange-300" },
        violet: { bg: "bg-violet-50 dark:bg-violet-900/20", icon: "text-violet-600 dark:text-violet-400", text: "text-violet-700 dark:text-violet-300" },
        slate: { bg: "bg-slate-50 dark:bg-slate-800/50", icon: "text-slate-500 dark:text-slate-400", text: "text-slate-600 dark:text-slate-400" },
    };
    const c = colorMap[color] || colorMap.primary;
    return (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
            <div className="flex items-center gap-3">
                <div className={`rounded-xl p-2.5 ${c.bg}`}>
                    <Icon className={`h-5 w-5 ${c.icon}`} />
                </div>
                <div className="min-w-0">
                    <p className="truncate text-sm text-slate-500 dark:text-slate-400">{label}</p>
                    <p className={`text-2xl font-bold ${c.text}`}>{value}</p>
                    {sub && <p className="text-xs text-slate-400 dark:text-slate-500">{sub}</p>}
                </div>
            </div>
        </div>
    );
};

// ============================================================================
// Helpers
// ============================================================================

const formatCurrency = (amount: number | null | undefined, currency = "NGN") => {
    if (amount == null) return "—";
    return new Intl.NumberFormat("en-NG", { style: "currency", currency, maximumFractionDigits: 0 }).format(amount);
};

const formatTime = (dateStr: string | null | undefined) => {
    if (!dateStr) return "—";
    try {
        return new Intl.DateTimeFormat("en-NG", { hour: "2-digit", minute: "2-digit", second: "2-digit" }).format(new Date(dateStr));
    } catch {
        return dateStr;
    }
};

// ============================================================================
// Component
// ============================================================================

export const LiveDashboardRoute = () => {
    const { data, isLoading, error, refetch, isFetching } = useQuery({
        queryKey: ["live-platform-stats"],
        queryFn: getLivePlatformStats,
        refetchInterval: 30_000, // auto-refresh every 30s
    });

    const stats = data?.data;

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center dark:border-rose-800 dark:bg-rose-900/20">
                <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-rose-400" />
                <h3 className="mt-3 text-lg font-semibold text-rose-700 dark:text-rose-300">Failed to load live stats</h3>
                <p className="mt-1 text-sm text-rose-600 dark:text-rose-400">{(error as Error).message}</p>
            </div>
        );
    }

    if (!stats) return null;

    return (
        <div className="space-y-8">
            {/* Refresh bar */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                    <BoltIcon className="h-4 w-4 text-emerald-500" />
                    <span>Auto-refreshing every 30s</span>
                    {stats.timestamp && (
                        <span className="text-slate-400">· Last updated {formatTime(stats.timestamp)}</span>
                    )}
                </div>
                <Button variant="secondary" size="sm" onClick={() => refetch()} disabled={isFetching}>
                    <ArrowPathIcon className={`mr-1.5 h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
                    Refresh
                </Button>
            </div>

            {/* Drivers Section */}
            <div>
                <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">Drivers</h2>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                    <StatCard label="Online" value={stats.drivers.online} icon={UsersIcon} color="emerald" />
                    <StatCard label="Available" value={stats.drivers.available} icon={UsersIcon} color="blue" />
                    <StatCard label="On Trip" value={stats.drivers.onTrip} icon={TruckIcon} color="amber" />
                    <StatCard label="Registered" value={stats.drivers.totalRegistered} icon={UsersIcon} color="slate" />
                </div>
            </div>

            {/* Trips Section */}
            <div>
                <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">Trips</h2>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
                    <StatCard label="Active" value={stats.trips.active} icon={MapPinIcon} color="primary" />
                    <StatCard label="Pending" value={stats.trips.pending} icon={ClockIcon} color="amber" />
                    <StatCard label="In Progress" value={stats.trips.inProgress} icon={TruckIcon} color="blue" />
                    <StatCard label="Completed Today" value={stats.trips.completedToday} icon={CheckCircleIcon} color="emerald" />
                    <StatCard label="Cancelled Today" value={stats.trips.cancelledToday} icon={XCircleIcon} color="rose" />
                </div>
                <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
                    <StatCard label="Started" value={stats.trips.started} icon={BoltIcon} color="blue" />
                    <StatCard label="Driver Arrived" value={stats.trips.driverArrived} icon={MapPinIcon} color="violet" />
                    <StatCard label="Picked Up" value={stats.trips.pickedUp} icon={UsersIcon} color="emerald" />
                    <StatCard label="Started (Last Hour)" value={stats.trips.startedLastHour} icon={ClockIcon} color="orange" />
                </div>
            </div>

            {/* Bookings Section */}
            <div>
                <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">Bookings</h2>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                    <StatCard label="Pending" value={stats.bookings.pending} icon={ClockIcon} color="amber" />
                    <StatCard label="Confirmed" value={stats.bookings.confirmed} icon={CheckCircleIcon} color="emerald" />
                    <StatCard label="Last Hour" value={stats.bookings.lastHour} icon={ClockIcon} color="blue" />
                    <StatCard label="Today" value={stats.bookings.today} icon={MapPinIcon} color="primary" />
                </div>
            </div>

            {/* Rides & Searches */}
            <div className="grid gap-8 lg:grid-cols-2">
                <div>
                    <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">Ride Offers</h2>
                    <div className="grid grid-cols-2 gap-4">
                        <StatCard label="Available" value={stats.rides.available} icon={TruckIcon} color="emerald" />
                        <StatCard label="Full" value={stats.rides.full} icon={TruckIcon} color="slate" />
                        <StatCard label="In Progress" value={stats.rides.inProgress} icon={TruckIcon} color="blue" />
                        <StatCard label="Created Today" value={stats.rides.createdToday} icon={BoltIcon} color="primary" />
                    </div>
                </div>
                <div>
                    <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">Ride Searches & Revenue</h2>
                    <div className="grid grid-cols-2 gap-4">
                        <StatCard label="Active Searches" value={stats.rideSearches.activeSearches} icon={MagnifyingGlassIcon} color="violet" />
                        <StatCard label="Searches (Last Hour)" value={stats.rideSearches.lastHour} icon={MagnifyingGlassIcon} color="blue" />
                        <StatCard label="Revenue Today" value={formatCurrency(stats.revenue.today)} icon={CurrencyDollarIcon} color="emerald" />
                        <StatCard label="Commission Today" value={formatCurrency(stats.revenue.commissionToday)} icon={CurrencyDollarIcon} color="amber" />
                    </div>
                </div>
            </div>
        </div>
    );
};
